import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { faceIdentityRoute } from '../utils/faceIdentityRoutes';
import { MapPin } from 'lucide-react';
import { startGuestEntrySession, clearGuestFlowAuth } from '../../shared/guestFlowAuth';
import { clearDeviceLocationCache } from '../../shared/deviceLocationCache';
import '../styles/FaceIdentityLuxTheme.css';
import './FaceIdentityGuestEntryPage.css';

const DENIED_MESSAGE =
  'Location access is blocked. Open your browser settings, allow location for this site, then try again.';

export default function FaceIdentityGuestEntryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const franchiseRef = useMemo(
    () =>
      String(
        params.get('franchise_code') ||
          params.get('franchiseCode') ||
          params.get('franchise_id') ||
          params.get('franchiseId') ||
          '',
      ).trim(),
    [params],
  );
  const [phase, setPhase] = useState('verifying');
  const [error, setError] = useState('');

  const beginCheckIn = useCallback(async () => {
    if (!franchiseRef) {
      setPhase('error');
      setError('Invalid QR link — franchise is missing.');
      return;
    }

    setPhase('verifying');
    setError('');

    try {
      clearDeviceLocationCache();
      await startGuestEntrySession(franchiseRef);
      navigate(faceIdentityRoute('scan', location.pathname), { replace: true });
    } catch (err) {
      const message = err?.message || 'Could not start check-in. Please try again at the store.';
      const needsLocation =
        err?.code === 'DEVICE_LOCATION_REQUIRED' ||
        err?.code === 'OUTSIDE_GEOFENCE' ||
        /permission denied|location permission|blocked|gps|geofence|location/i.test(message);
      const denied = /permission denied|blocked/i.test(message);
      if (needsLocation) {
        setPhase('location');
        setError(denied ? DENIED_MESSAGE : message);
        return;
      }
      setPhase('error');
      setError(message);
    }
  }, [franchiseRef, location.pathname, navigate]);

  useEffect(() => {
    clearGuestFlowAuth();
    clearDeviceLocationCache();

    if (!franchiseRef) {
      setPhase('error');
      setError('Invalid QR link — franchise is missing.');
      return undefined;
    }

    void beginCheckIn();
    return undefined;
  }, [franchiseRef, beginCheckIn]);

  const handleEnableLocation = () => {
    setError('');
    void beginCheckIn();
  };

  const title =
    phase === 'error'
      ? 'Unable to continue'
      : phase === 'verifying'
        ? 'Starting check-in…'
        : 'Enable location to check in';

  const lede =
    phase === 'error'
      ? error
      : phase === 'verifying'
        ? 'Please wait while we prepare your guest session.'
        : error ||
          'This store requires location to check in. Tap below and allow access when your browser asks.';

  return (
    <div className="fi-guest-entry fi-lux-theme">
      <div className="fi-guest-entry__card">
        <p className="fi-guest-entry__eyebrow">Guest check-in</p>
        {phase === 'location' ? (
          <div className="fi-guest-entry__icon" aria-hidden="true">
            <MapPin size={28} />
          </div>
        ) : null}
        <h1 className="fi-guest-entry__title">{title}</h1>
        <p className="fi-guest-entry__lede">{lede}</p>
        {phase === 'location' ? (
          <button type="button" className="fi-guest-entry__btn" onClick={handleEnableLocation}>
            Allow location access
          </button>
        ) : null}
        {phase === 'error' ? (
          <button type="button" className="fi-guest-entry__btn" onClick={handleEnableLocation}>
            Try again
          </button>
        ) : null}
        {phase === 'verifying' ? (
          <div className="fi-guest-entry__spinner" aria-hidden="true" />
        ) : null}
      </div>
    </div>
  );
}
